import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { Check, Loader2, XCircle, ArrowRight, Calendar } from "lucide-react";
import { trackSubscribe } from "@/utils/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Membership prices for tracking
const MEMBERSHIP_PRICES = {
  silver: 240,
  bronze: 440,
  gold: 800
};

export default function MembershipSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [membershipData, setMembershipData] = useState(null);
  const [tracked, setTracked] = useState(false);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/memberships/checkout-status/${sessionId}`);
      
      if (response.data.status === "complete") {
        setStatus("success");
        setMembershipData(response.data);
        
        // Track subscription with Meta Pixel (only once)
        if (!tracked) {
          const tier = response.data.tier;
          const price = MEMBERSHIP_PRICES[tier] || 440;
          trackSubscribe(price, 'AUD', price * 12); // LTV = 1 year
          setTracked(true);
        }
        return;
      }

      // Continue polling
      setTimeout(() => pollStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking status:", error);
      setTimeout(() => pollStatus(attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-dream-bg flex items-center justify-center px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-8 max-w-lg w-full text-center"
      >
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-dream-violet mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Processing Your Membership...
            </h1>
            <p className="text-white/60">
              Please wait while we confirm your subscription.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-4">
              Welcome to DMRG! 🎉
            </h1>
            <p className="text-white/60 mb-6">
              Your membership has been activated successfully. Check your email for confirmation and details.
            </p>
            
            {membershipData?.tier && (
              <div className="bg-dream-surface rounded-xl p-4 mb-6">
                <p className="text-dream-violet font-mono text-sm">Your Plan</p>
                <p className="text-white font-heading text-xl font-bold capitalize">
                  {membershipData.tier} Membership
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Link to="/booking" className="block">
                <Button className="w-full bg-dream-violet text-white hover:bg-dream-violet-glow py-6 font-bold">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Your First Session
                </Button>
              </Link>
              <Link to="/" className="block">
                <Button variant="outline" className="w-full border-dream-border text-white hover:bg-white/10 py-6">
                  Return Home
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Something Went Wrong
            </h1>
            <p className="text-white/60 mb-6">
              We couldn&apos;t confirm your membership. Please contact us if you were charged.
            </p>
            <div className="space-y-3">
              <Link to="/memberships" className="block">
                <Button className="w-full bg-dream-violet text-white hover:bg-dream-violet-glow py-6">
                  Try Again
                </Button>
              </Link>
              <Link to="/contact" className="block">
                <Button variant="outline" className="w-full border-dream-border text-white hover:bg-white/10 py-6">
                  Contact Support
                </Button>
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
